<script async setup>
import {onMounted, defineProps, ref, onUnmounted, nextTick} from "vue";
import {tns} from "tiny-slider";

const props = defineProps({
    containerClass: {
        type: String,
        default: "",
    },
    data: {
        type: Array,
        default: () => [],
    },
    autoplay: {
        type: Boolean,
        default: false
    },
});

const sliderApi = ref(null);
let ro = null;

function matchSlideHeights() {
  const items = document.querySelectorAll("#ts-reviews-slider .tns-item");
  if (!items.length) return;
  let maxH = 0;
  items.forEach((it) => {
    const child = it.querySelector("*");
    if (!child) return;
    child.style.height = "auto";
    maxH = Math.max(maxH, child.offsetHeight);
  });
  items.forEach((it) => {
    const child = it.querySelector("*");
    if (child) child.style.height = `${maxH}px`;
  });
}

const restartAutoplay = () => {
  if (!props.autoplay || !sliderApi.value) return;
  sliderApi.value.pause();
  requestAnimationFrame(() => sliderApi.value?.play());
};

const prevSlide = () => { sliderApi.value?.goTo("prev"); restartAutoplay(); };
const nextSlide = () => { sliderApi.value?.goTo("next"); restartAutoplay(); };

onMounted(async () => {
  sliderApi.value = tns({
    container: "#ts-reviews-slider",
    items: 1,
    gutter: 30,
    slideBy: 1,
    autoplay: props.autoplay,
    autoplayTimeout: 6000,
    autoplayHoverPause: true,
    autoplayButtonOutput: false,
    rewind: true,
    loop: false,
    mouseDrag: true,
    speed: 400,
    controls: false,
    nav: false,
    center: false,
    responsive: {
      0: { items: 1, center: false },
      700: { items: 2, center: false },
      1200: { items: 3, center: false },
    },
  });

  await nextTick();
  requestAnimationFrame(matchSlideHeights);
  sliderApi.value.events.on("indexChanged", () => {
    matchSlideHeights();
  });
  sliderApi.value.events.on("dragEnd", () => {
    restartAutoplay();
    matchSlideHeights();
  });
  const container = document.getElementById("ts-reviews-slider");
  if (window.ResizeObserver && container) {
    ro = new ResizeObserver(() => matchSlideHeights());
    ro.observe(container);
  }
  window.addEventListener("resize", matchSlideHeights);
});

onUnmounted(() => {
  window.removeEventListener("resize", matchSlideHeights);
  if (ro) {ro.disconnect(); ro = null;}
  if (sliderApi.value) {
    sliderApi.value.destroy();
    sliderApi.value = null;
  }
});
</script>

<template>
    <div id="ts-reviews-slider" v-if="props.data.length">
        <div v-for="(item) in props.data">
            <div class="min-h-46 bg-ts-white border border-white rounded-2xl p-4 flex flex-col items-center my-6">
                <div class="flex justify-between w-full pb-2 border-b-2 border-ts-black">
                    <p class="text-sm whitespace-nowrap">{{ item.author }}</p>
                    <kd-stars :rating="item.rating" :size="20" />
                </div>
                <p class="pt-4 flex-1">
                    {{ item.text }}
                    <template v-if="item.link_enabled && item.truncated && item.url && item.text">
                        <a
                            :href="item.url"
                            target="_blank"
                            rel="noopener nofollow ugc noreferrer"
                            class="underline underline-offset-2"
                        >
                            {{ item.link_text }}
                        </a>
                    </template>
                </p>
                <p class="text-left pt-4 w-full"> {{ item.date }}</p>
            </div>
        </div>
    </div>
    <div class="flex justify-end mb-4 gap-x-4">
        <button class="cursor-pointer" @click="prevSlide">
            <kd-icon-arrow-left fill="black" size="30" />
        </button>
        <button class="cursor-pointer" @click="nextSlide">
            <kd-icon-arrow-right fill="black" size="30" />
        </button>
    </div>
</template>
